import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import {
  clampZoom,
  fitPageZoom,
  fitWidthZoom,
  parseSheetMeta,
  rectToPx,
  rowZoom,
  scrollToRect,
  stripUnitHeight,
  zoomAt,
  type PxRect,
} from './geometry';
import { isContinuation, type SheetTarget } from './protocol';

/** Цвета рамок — на белом листе, одинаковые в светлой и тёмной теме: строка —
 *  оранжевая, шапка таблицы — синяя (решение 15). */
const ROW_COLOR = '#F57C00';
const HEAD_COLOR = '#1976D2';

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Tools = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ZoomText = styled.span`
  min-width: 48px;
  text-align: center;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Scroller = styled.div<{ $drag: boolean }>`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: ${({ theme }) => theme.colors.surface};
  cursor: ${({ $drag }) => ($drag ? 'grabbing' : 'grab')};
  user-select: none;
`;

/** Лист и рамки: размер — картинка × масштаб. */
const Frame = styled.div.attrs<{ $w: number; $h: number }>(({ $w, $h }) => ({
  style: { width: `${$w}px`, height: `${$h}px` },
}))<{ $w: number; $h: number }>`
  position: relative;
  background: #fff;

  img {
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
`;

/** Рамка поверх листа: положение — пиксели листа × масштаб. */
const Box = styled.div.attrs<{ $r: PxRect; $zoom: number }>(({ $r, $zoom }) => ({
  style: {
    left: `${$r.left * $zoom - 3}px`,
    top: `${$r.top * $zoom - 3}px`,
    width: `${$r.width * $zoom + 6}px`,
    height: `${$r.height * $zoom + 6}px`,
  },
}))<{ $r: PxRect; $zoom: number; $color: string; $dashed?: boolean }>`
  position: absolute;
  border: 3px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')} ${({ $color }) => $color};
  border-radius: 2px;
  box-shadow: 0 0 0 9999px
    ${({ $color }) => ($color === ROW_COLOR ? 'rgba(0, 0, 0, 0.06)' : 'transparent')};
  pointer-events: none;
`;

/** Закреплённая полоска шапки таблицы на листе-продолжении (решение 17):
 *  прилипает к верху окна, по горизонтали прокручивается вместе с листом. */
const Strip = styled.div.attrs<{ $w: number; $h: number }>(({ $w, $h }) => ({
  style: { width: `${$w}px`, height: `${$h}px` },
}))<{ $w: number; $h: number }>`
  position: sticky;
  top: 0;
  z-index: 2;
  overflow: hidden;
  background: #fff;
  border-bottom: 3px solid ${HEAD_COLOR};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
`;

/** Лист без геометрии — картинкой в натуральную величину, без рамок. */
const PlainImg = styled.img`
  display: block;
  max-width: none;
  pointer-events: none;
`;

const StripImg = styled.img.attrs<{ $top: number; $w: number; $h: number }>(({ $top, $w, $h }) => ({
  style: { top: `${-$top}px`, width: `${$w}px`, height: `${$h}px` },
}))<{ $top: number; $w: number; $h: number }>`
  position: absolute;
  left: 0;
  max-width: none;
  pointer-events: none;
`;

const StripLabel = styled.span`
  position: absolute;
  right: 6px;
  top: 4px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: ${HEAD_COLOR};
  border-radius: 3px;
`;

interface Img {
  url: string;
  meta: string | null;
}

interface Props {
  target: SheetTarget;
  /** Номер показа: новый показ той же строки снова прокручивает к ней. */
  shown: number;
  image: Img;
  /** Лист шапки таблицы (продолжение таблицы) — для закреплённой полоски. */
  headImage?: Img;
}

/** Лист РД целиком с рамкой строки и шапки; масштаб — кнопками и колесом с
 *  Ctrl, перетаскивание мышью двигает лист. При смене строки окно само
 *  подбирает масштаб под таблицу (если сменился лист) и прокручивает к строке. */
export function SheetView({ target, shown, image, headImage }: Props) {
  const meta = useMemo(() => parseSheetMeta(image.meta), [image.meta]);
  const headMeta = useMemo(() => parseSheetMeta(headImage?.meta), [headImage?.meta]);
  const scroller = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [drag, setDrag] = useState(false);
  const fittedSheet = useRef<string | null>(null);
  const pendingScroll = useRef<{ left: number; top: number } | null>(null);

  const cont = isContinuation(target);
  // Шапка на этом же листе — синей рамкой; на другом — полоской сверху.
  const headHere = target.headBox && !cont ? target.headBox : null;
  const rowPx = meta && target.box ? rectToPx(meta, target.box) : null;
  const headPx = meta && headHere ? rectToPx(meta, headHere) : null;

  const z = zoom ?? 1;
  const ptToCss = meta ? (z * meta.pxWidth) / meta.width : 0;
  // Высота полоски шапки при масштабе 1: известна сразу — по рамке шапки и
  // геометрии листа; картинка шапки, когда придёт, её только уточняет. Место
  // под полоску резервируется до загрузки шапки: первая прокрутка к строке
  // уже учитывает полоску, и лист не сдвигается, когда шапка догрузится.
  const stripUnit = useMemo(
    () => (cont && meta && target.headBox ? stripUnitHeight(meta, target.headBox, headMeta) : 0),
    [cont, meta, target.headBox, headMeta],
  );
  const strip = useMemo(() => {
    if (!cont || !headImage || !headMeta || !target.headBox || !meta) return null;
    const hp = rectToPx(headMeta, target.headBox);
    const hz = ptToCss / (headMeta.pxWidth / headMeta.width);
    return {
      url: headImage.url,
      top: hp.top * hz,
      w: headMeta.pxWidth * hz,
      imgH: headMeta.pxHeight * hz,
    };
  }, [cont, headImage, headMeta, target.headBox, meta, ptToCss]);

  const viewSize = useCallback(() => {
    const el = scroller.current;
    return { width: el?.clientWidth ?? 1200, height: el?.clientHeight ?? 800 };
  }, []);

  /** Масштаб и прокрутка разом: тот же масштаб — прокрутить сразу (перерисовки
   *  не будет), новый — после перерисовки (`useLayoutEffect`). */
  function applyView(next: number, scroll: { left: number; top: number }) {
    if (next === zoom) {
      scroller.current?.scrollTo(scroll);
      return;
    }
    pendingScroll.current = scroll;
    setZoom(next);
  }

  /** Высота полоски шапки при масштабе `next` (она пропорциональна масштабу);
   *  до загрузки картинки шапки — расчётная, место под полоску уже занято. */
  function stripHeight(next: number): number {
    return stripUnit * next;
  }

  // Новая строка: масштаб под таблицу (если сменился лист) и прокрутка к строке.
  // Полоска шапки — сразу расчётной высоты; если картинка шапки уточнила
  // высоту (другой формат листа шапки), прокрутка повторяется с ней — строка не
  // должна уйти под полоску. Та же высота — прокрутка не повторяется.
  const sheetKey = `${target.revisionId}|${target.file ?? ''}|${target.page}`;
  const rowKey = `${sheetKey}|${target.positionId ?? ''}|${target.box?.join(',') ?? ''}|${shown}`;
  const stripKey = Math.round(stripUnit * 10);
  useEffect(() => {
    if (!meta) return;
    const view = viewSize();
    let next = zoom;
    if (fittedSheet.current !== sheetKey || next === null) {
      next = rowZoom(meta, headHere ?? target.box, view.width);
      fittedSheet.current = sheetKey;
    }
    const stripH = stripHeight(next);
    const content = { width: meta.pxWidth * next, height: meta.pxHeight * next + stripH };
    applyView(
      next,
      rowPx
        ? scrollToRect(rowPx, next, view, content, stripH)
        : { left: headPx ? Math.max(0, headPx.left * next - 8) : 0, top: 0 },
    );
    // Только при смене строки или листа, новом показе, приходе геометрии и
    // уточнении высоты полоски шапки: ручной масштаб человека на том же листе
    // не сбрасываем.
  }, [rowKey, meta, stripKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const el = scroller.current;
    const s = pendingScroll.current;
    if (!el || !s) return;
    pendingScroll.current = null;
    el.scrollTo({ left: s.left, top: s.top });
  });

  // Колесо с Ctrl — масштаб вокруг курсора.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey || zoom === null) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const res = zoomAt(
        zoom,
        e.deltaY < 0 ? 1.1 : 1 / 1.1,
        { x: e.clientX - r.left, y: e.clientY - r.top },
        {
          left: el.scrollLeft,
          top: el.scrollTop,
        },
      );
      applyView(res.zoom, { left: res.left, top: res.top });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Перетаскивание листа мышью.
  const dragFrom = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const el = scroller.current;
    // По самому прокручиваемому полю (его полосы прокрутки) — не перетаскивание.
    if (!el || e.button !== 0 || e.target === el) return;
    dragFrom.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    el.setPointerCapture(e.pointerId);
    setDrag(true);
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const el = scroller.current;
    const d = dragFrom.current;
    if (!el || !d) return;
    el.scrollLeft = d.left - (e.clientX - d.x);
    el.scrollTop = d.top - (e.clientY - d.y);
  }
  function onPointerUp() {
    dragFrom.current = null;
    setDrag(false);
  }

  function zoomBy(factor: number) {
    const el = scroller.current;
    if (!el || zoom === null) return;
    const res = zoomAt(
      zoom,
      factor,
      { x: el.clientWidth / 2, y: el.clientHeight / 2 },
      {
        left: el.scrollLeft,
        top: el.scrollTop,
      },
    );
    applyView(res.zoom, { left: res.left, top: res.top });
  }

  function fit(kind: 'width' | 'page') {
    if (!meta) return;
    const view = viewSize();
    const next =
      kind === 'width'
        ? fitWidthZoom(meta, view.width)
        : fitPageZoom(meta, view.width, view.height);
    applyView(next, { left: 0, top: 0 });
  }

  function toRow() {
    if (!meta || !rowPx) return;
    const view = viewSize();
    const next = rowZoom(meta, headHere ?? target.box, view.width);
    const stripH = stripHeight(next);
    applyView(
      next,
      scrollToRect(
        rowPx,
        next,
        view,
        { width: meta.pxWidth * next, height: meta.pxHeight * next + stripH },
        stripH,
      ),
    );
  }

  return (
    <Wrap>
      <Tools>
        <Button
          $variant="secondary"
          $size="sm"
          onClick={() => zoomBy(1 / 1.25)}
          aria-label="Мельче"
        >
          −
        </Button>
        <ZoomText>{zoom === null ? '—' : `${Math.round(clampZoom(z) * 100)}%`}</ZoomText>
        <Button $variant="secondary" $size="sm" onClick={() => zoomBy(1.25)} aria-label="Крупнее">
          +
        </Button>
        <Button $variant="ghost" $size="sm" onClick={() => fit('width')}>
          По ширине
        </Button>
        <Button $variant="ghost" $size="sm" onClick={() => fit('page')}>
          Лист целиком
        </Button>
        {rowPx && (
          <Button $variant="ghost" $size="sm" onClick={toRow}>
            К строке
          </Button>
        )}
      </Tools>
      {!meta ? (
        <Note>Размер листа не пришёл — лист показан без рамок.</Note>
      ) : !target.box ? (
        <Note>
          У этой строки нет рамки на листе — лист открыт без подсветки. Рамки строк текстовых таблиц
          появятся после переобработки РД.
        </Note>
      ) : target.boxIsAnchor ? (
        <Note>
          Строка внесена вручную («строка не распознана программой»): своей рамки на листе у неё нет
          — пунктиром подсвечена строка, после которой она стоит.
        </Note>
      ) : null}
      <Scroller
        ref={scroller}
        $drag={drag}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {meta && stripUnit > 0 && (
          <Strip
            $w={meta.pxWidth * z}
            $h={stripUnit * z}
            title={`Шапка таблицы — лист ${target.headPage}`}
          >
            {strip && (
              <StripImg src={strip.url} alt="" $top={strip.top} $w={strip.w} $h={strip.imgH} />
            )}
            <StripLabel>шапка · стр. PDF {target.headPage}</StripLabel>
          </Strip>
        )}
        {meta ? (
          <Frame $w={meta.pxWidth * z} $h={meta.pxHeight * z}>
            <img src={image.url} alt={`Лист РД, стр. PDF ${target.page}`} draggable={false} />
            {headPx && <Box $r={headPx} $zoom={z} $color={HEAD_COLOR} />}
            {rowPx && (
              <Box $r={rowPx} $zoom={z} $color={ROW_COLOR} $dashed={target.boxIsAnchor} />
            )}
          </Frame>
        ) : (
          <PlainImg src={image.url} alt={`Лист РД, стр. PDF ${target.page}`} draggable={false} />
        )}
      </Scroller>
    </Wrap>
  );
}
